import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useUser } from "@clerk/react";
import { apiFetch } from "../utils/apiClient";
import ConflictReportView from "../components/ConflictReportView";

export default function ConflictReportPage() {
  const { reportId } = useParams();
  const { user, isLoaded, isSignedIn } = useUser();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!report) return;
    const prevTitle = document.title;
    const prefix = report.report_type === "vendor_scan" ? "PPW Vendor Conflict Report" : "PPW Provider Conflict Report";
    document.title = `${prefix}_${report.id}`;
    return () => { document.title = prevTitle; };
  }, [report]);

  useEffect(() => {
    if (!isLoaded || !isSignedIn || !user?.id || !reportId) return;
    setLoading(true);
    setError(null);
    apiFetch(`/api/conflict-report-status?userId=${user.id}&reportId=${reportId}`)
      .then(async (res) => {
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || "Failed to load report");
        }
        return res.json();
      })
      .then(setReport)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [isLoaded, isSignedIn, user?.id, reportId]);

  if (!isLoaded || loading) {
    return (
      <main className="max-w-3xl mx-auto px-4 py-10">
        <p className="text-white/40 text-sm text-center py-12">Loading report…</p>
      </main>
    );
  }

  if (!isSignedIn) {
    return (
      <main className="max-w-3xl mx-auto px-4 py-10 text-center">
        <p className="text-white/60 text-sm">Sign in to view this report.</p>
      </main>
    );
  }

  if (error) {
    return (
      <main className="max-w-3xl mx-auto px-4 py-10 text-center">
        <p className="text-red-400 text-sm mb-2">{error}</p>
        <Link to="/my-providers" className="text-gold-400 text-xs hover:text-gold-300">← Back to My Providers</Link>
      </main>
    );
  }

  if (report && report.status === "failed") {
    return (
      <main className="max-w-3xl mx-auto px-4 py-10 text-center">
        <p className="text-red-400 text-sm mb-2">This scan failed to complete.</p>
        <p className="text-white/40 text-xs mb-4">{report.error}</p>
        <Link to="/my-providers" className="text-gold-400 text-xs hover:text-gold-300">← Back to My Providers</Link>
      </main>
    );
  }

  return (
    <main>
      <div className="print:hidden max-w-3xl mx-auto px-4 pt-6">
        <Link to="/my-providers" className="text-white/40 hover:text-white/70 text-sm transition-colors">
          ← My Providers
        </Link>
      </div>
      <ConflictReportView report={report} userId={user.id} />
    </main>
  );
}